﻿import { ApiClient, ApiResponse, BaseApiResponse, HttpResponse, VindralChannelInformation } from '@core/swagger-client';
import {
    ChatChannelIds,
    ChatMessageRelationType,
    ClientType,
    IngestType,
    LiveStatus,
    ParticipantSession,
    PlayerStatsSample,
    SpeakerStatus,
    VideoLayout,
    WebinarStatus
} from '@core/types';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import type { IWebrtcIngestStats } from '@vindral/webrtc-ingest-sdk';
import { Duration } from 'luxon';
import { call, delay, getContext, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { isAuthRejection, type MoqFallbackReason, type MoqPublisherStatsSample } from '../moq';
import { LiveWebinarConnection } from '../signal-r/live-webinar-connection';
import {
    IngestInformation,
    JoinWebinarRequest,
    WebinarExtendedResult,
    chatMessageRejected,
    claimActivePresenter,
    closePoll,
    dataConnectionStatusUpdated,
    deleteChatMessage,
    extendWebinarDuration,
    forwardChatMessage,
    hidePoll,
    joinChatChannel,
    joinWebinar,
    kickSpeaker,
    leaveChatChannel,
    leaveWebinar,
    liveStatusUpdated,
    markChatChannelAsRead,
    markChatChannelAsUnread,
    moqFallbackRequested,
    moqFallbackTriggered,
    mySpeakerStatusUpdated,
    notifySpeaker,
    playerStatsSampled,
    refreshRtmpIngestInformation,
    refreshSrtIngestInformation,
    refreshVindralChannel,
    replyChatMessage,
    rtmpIngestInformationUpdated,
    sendChatMessage,
    sendMoqPublisherStats,
    sendWebrtcIngestStats,
    serverTimeUpdated,
    setActiveChapter,
    setActiveSlide,
    setBroadcastState,
    setLastServerAction,
    localGraceExpired,
    broadcastReconciled,
    setLiveStatus,
    setSpeakerStatus,
    setIngestType,
    setVideoLayout,
    setWebinarExtendedResult,
    showReconnectWarning,
    srtIngestInformationUpdated,
    startPoll,
    startWebinar,
    stopWebinar,
    updateNickname,
    vindralChannelUpdated,
    voteOnPoll
} from '../slices/live-webinar-slice';
import { RootState } from '../store';

// TODO: Handle errors better

function* handleJoinWebinar(action: PayloadAction<JoinWebinarRequest>) {
    const signalR: LiveWebinarConnection = yield getContext('liveConnection');
    const api: ApiClient = yield getContext('api');
    try {
        let result: HttpResponse<ApiResponse<string>> | undefined = undefined;
        if (action.payload.anonymousId) {
            result = yield call(api.anonymous.joinLiveWebinar, {
                anonymousId: action.payload.anonymousId,
                authorizationCode: action.payload.anonymousAuthorizationCode,
                webinarId: action.payload.webinarId
            });
        } else if (action.payload.linkCode) {
            result = yield call(api.watchLink.joinLiveWebinar, { linkCode: action.payload.linkCode });
        } else {
            result = yield call(api.liveWebinar.joinWebinar, action.payload);
        }

        if (result?.data?.success !== true || !result?.data?.data) throw result;
        yield call([signalR, signalR.connect], result.data.data);
    } catch (error: any) {
        yield put(liveStatusUpdated(LiveStatus.Error));
        let response: BaseApiResponse | undefined = undefined;
        if ('error' in error) {
            response = error.error as BaseApiResponse;
        }
        if ('data' in error) {
            response ??= error.data as BaseApiResponse;
        }
        response ??= {
            statusCode: 500,
            message: error.toString(),
            success: false,
            errorCode: 'UnexpectedError'
        };
        yield put(dataConnectionStatusUpdated({ status: LiveStatus.Error, error: response }));
    }
}

function* handleLeaveWebinar() {
    const signalR: LiveWebinarConnection = yield getContext('liveConnection');
    yield call([signalR, signalR.disconnect]);
}

function* handleSetLiveStatus(action: PayloadAction<LiveStatus>) {
    try {
        const { isPresenter, isActivePresenter } = yield select((state: RootState) => state.liveWebinar);
        if (!isPresenter && !isActivePresenter) {
            // Only active presenters can set the live status
            yield put(
                setLastServerAction({
                    method: 'setLiveStatus',
                    result: { statusCode: 401, success: false, message: 'Only active presenters can set live status', errorCode: 'UnauthorizedAccessException' }
                })
            );
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.setLiveStatus], action.payload);
        yield put(setLastServerAction({ method: 'setLiveStatus', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'setLiveStatus',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleStartWebinar() {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.startWebinar]);
        yield put(setLastServerAction({ method: 'startWebinar', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'startWebinar',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleStopWebinar() {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.stopWebinar]);
        yield put(setLastServerAction({ method: 'stopWebinar', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'stopWebinar',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleServerTimeUpdated(action: PayloadAction<string>) {
    const { vindralChannel, earlyStartOn } = yield select((state: RootState) => state.liveWebinar);
    const { status: webinarStatus } = yield select((state: RootState) => state.liveWebinar.webinarData);
    if (!earlyStartOn || !!vindralChannel || (webinarStatus !== WebinarStatus.Planned && webinarStatus !== WebinarStatus.Started)) {
        return;
    }

    const serverTime = new Date(action.payload);
    const earlyStartTime = new Date(earlyStartOn);
    if (serverTime < earlyStartTime) {
        return;
    }

    if (!vindralChannel) {
        yield put(refreshVindralChannel());
    }
}

function* handleSetBroadcastState(action: PayloadAction<'broadcasting' | 'initializing' | 'not_broadcasting' | 'error'>) {
    const { isPresenter, isActivePresenter, previousBroadcastState, mediaInterruptionSeq } = yield select(
        (state: RootState) => state.liveWebinar
    );
    if (!isPresenter) {
        return;
    }

    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');

        if (action.payload !== 'broadcasting') {
            // The reducer has already closed the gate. Only a fall from 'broadcasting' is worth telling
            // anyone about: the publisher's own start-up states are not a loss and must not raise the
            // connection dialog. The call fails silently when the connection is down, and that is the
            // case this has to survive -- the server hears about it on reconnect.
            if (previousBroadcastState === 'broadcasting') {
                yield put(showReconnectWarning());
                const result: BaseApiResponse = yield call([signalR, signalR.reportMediaLost]);
                yield put(setLastServerAction({ method: 'reportMediaLost', result }));
            }
            return;
        }

        // Video is flowing. Before go-live this is what makes the webinar startable: the server takes it
        // from Offline to Paused, which the Start button requires. After an interruption it is what
        // tells the server the video is back -- never a resume, that stays the presenter's own decision,
        // because Vindral needs an unknown amount of time to rebuild its transcoder. Only the active
        // presenter's report counts; the server ignores anyone else's.
        if (!isActivePresenter) return;

        const result: ApiResponse<LiveStatus> = yield call([signalR, signalR.reportMediaRestored]);
        yield put(setLastServerAction({ method: 'reportMediaRestored', result }));
        if (result?.success) {
            // The server has been told; its status governs again. Its verdict rides on the reply, and is
            // applied before the gate opens: the same status also arrives as an event, but later, and
            // opening on a bare "ok" put real video on the wire against a stale Live for that gap.
            if (result.data !== undefined) yield put(liveStatusUpdated(result.data));
            yield put(broadcastReconciled(mediaInterruptionSeq));
        }
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'reportMediaState',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

/**
 * How long we keep publishing the real programme after losing the server.
 *
 * Slightly longer than the server's own grace so that in the ordinary case its verdict lands first and
 * this never has to fire. Measured on the monotonic clock: a browser that was suspended must not be
 * able to come back and claim the deadline has not passed.
 */
const LOCAL_GRACE_MS = 6000;

function* handleBroadcastConnectionLost() {
    const start = performance.now();
    yield delay(LOCAL_GRACE_MS);

    const { dataConnection } = yield select((state: RootState) => state.liveWebinar);
    if (dataConnection?.status === LiveStatus.Live) {
        return; // We got back in time.
    }

    if (performance.now() - start < LOCAL_GRACE_MS) {
        return; // The clock was tampered with or the timer fired early; do not grant extra time.
    }

    yield put(localGraceExpired());
}

function* handleDataConnectionStatusUpdated(action: PayloadAction<{ status: LiveStatus; error?: BaseApiResponse }>) {
    const { isPresenter, isActivePresenter } = yield select((state: RootState) => state.liveWebinar);
    if (!isPresenter && !isActivePresenter) {
        // Only active presenters should see a reconnect warning
        return;
    }

    if (action.payload.status !== LiveStatus.Live) {
        yield put(showReconnectWarning());

        // Start our own clock. The server is running one too, but it starts when it notices we are
        // gone, which can be a good deal later than the moment we actually lost the connection. Only a
        // return to Live ends the wait: onreconnecting, onclose and the watchdog all dispatch Offline,
        // and any of those must not be mistaken for the reconnect.
        yield race({
            expired: call(handleBroadcastConnectionLost),
            reconnected: take((a: Action) => dataConnectionStatusUpdated.match(a) && a.payload.status === LiveStatus.Live)
        });
        return;
    }

    // Back on the connection. Tell the server which connection is ours now and what happened to our
    // video, and only once it has accepted that do we let its status govern us again. Retried a few
    // times: a failed report here would otherwise leave the presenter publishing black while the
    // server says live.
    const signalR: LiveWebinarConnection = yield getContext('liveConnection');
    for (let attempt = 0; attempt < 5; attempt++) {
        const { mediaInterrupted, mediaInterruptionSeq, broadcastState, dataConnection } = yield select(
            (state: RootState) => state.liveWebinar
        );
        if (dataConnection?.status !== LiveStatus.Live) return; // Lost it again; the next reconnect reconciles.

        try {
            const result: ApiResponse<LiveStatus> = yield call(
                [signalR, signalR.registerBroadcastConnection],
                mediaInterrupted,
                broadcastState === 'broadcasting'
            );
            if (result?.success) {
                // Verdict first, gate second -- see handleSetBroadcastState for why.
                if (result.data !== undefined) yield put(liveStatusUpdated(result.data));
                yield put(broadcastReconciled(mediaInterruptionSeq));
                return;
            }
            yield put(setLastServerAction({ method: 'registerBroadcastConnection', result }));
        } catch (e) {
            yield put(
                setLastServerAction({
                    method: 'registerBroadcastConnection',
                    result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
                })
            );
        }
        yield delay(1000 * (attempt + 1));
    }
}

function* handleJoinChatChannel(action: PayloadAction<string>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.joinChatChannel], action.payload);
        yield put(setLastServerAction({ method: 'joinChatChannel', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'joinChatChannel',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleLeaveChatChannel(action: PayloadAction<string>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.leaveChatChannel], action.payload);
        yield put(setLastServerAction({ method: 'leaveChatChannel', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'leaveChatChannel',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleSendChatMessage(action: PayloadAction<{ channelId: string; message: string }>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.sendChatMessage], action.payload.channelId, action.payload.message);
        yield put(setLastServerAction({ method: 'sendChatMessage', result }));
        if (!result.success) {
            yield put(chatMessageRejected({ channelId: action.payload.channelId, message: action.payload.message, errorCode: result.errorCode }));
        }
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'sendChatMessage',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
        yield put(chatMessageRejected({ channelId: action.payload.channelId, message: action.payload.message, errorCode: 'Exception' }));
    }
}

function* handleReplyChatMessage(action: PayloadAction<{ channelId: string; message: string; relatedMessageId: string }>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call(
            [signalR, signalR.replyChatMessage],
            action.payload.channelId,
            action.payload.message,
            action.payload.relatedMessageId
        );
        yield put(setLastServerAction({ method: 'replyChatMessage', result }));
        if (!result.success) {
            yield put(
                chatMessageRejected({
                    channelId: action.payload.channelId,
                    message: action.payload.message,
                    relatedMessageId: action.payload.relatedMessageId,
                    relationType: ChatMessageRelationType.reply,
                    errorCode: result.errorCode
                })
            );
        }
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'replyChatMessage',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
        yield put(
            chatMessageRejected({
                channelId: action.payload.channelId,
                message: action.payload.message,
                relatedMessageId: action.payload.relatedMessageId,
                relationType: ChatMessageRelationType.reply,
                errorCode: 'Exception'
            })
        );
    }
}

function* handleForwardChatMessage(action: PayloadAction<{ channelId: string; message: string; relatedMessageId: string }>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call(
            [signalR, signalR.forwardChatMessage],
            action.payload.channelId,
            action.payload.message,
            action.payload.relatedMessageId
        );
        yield put(setLastServerAction({ method: 'forwardChatMessage', result }));
        if (!result.success) {
            yield put(
                chatMessageRejected({
                    channelId: action.payload.channelId,
                    message: action.payload.message,
                    relatedMessageId: action.payload.relatedMessageId,
                    relationType: ChatMessageRelationType.forward,
                    errorCode: result.errorCode
                })
            );
        }
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'forwardChatMessage',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
        yield put(
            chatMessageRejected({
                channelId: action.payload.channelId,
                message: action.payload.message,
                relatedMessageId: action.payload.relatedMessageId,
                relationType: ChatMessageRelationType.forward,
                errorCode: 'Exception'
            })
        );
    }
}

function* handleDeleteChatMessage(action: PayloadAction<{ channelId: string; messageId: string }>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.deleteChatMessage], action.payload.channelId, action.payload.messageId);
        yield put(setLastServerAction({ method: 'deleteChatMessage', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'deleteChatMessage',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleSetActiveSlide(action: PayloadAction<number>) {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter && me?.clientType !== ClientType.slide_controller) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.setActiveSlide], action.payload);
        yield put(setLastServerAction({ method: 'setActiveSlide', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'setActiveSlide',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleSetActiveChapter(action: PayloadAction<string>) {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.setActiveChapter], action.payload);
        yield put(setLastServerAction({ method: 'setActiveChapter', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'setActiveChapter',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

// Exported for tests
export function* handleRefreshVindralChannel() {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const vindralChannel: ApiResponse<VindralChannelInformation | null> = yield call([signalR, signalR.refreshVindralChannel]);
        yield put(setLastServerAction({ method: 'refreshVindralChannel', result: vindralChannel }));
        // A failed refresh (e.g. NotConnected during a reconnect) must not clear the channel;
        // a successful answer without data means the booking is gone.
        if (vindralChannel.success) {
            yield put(vindralChannelUpdated(vindralChannel.data || null));
        }
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'refreshVindralChannel',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleRefreshRtmpIngestInformation() {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const rtmpIngestInformation: ApiResponse<IngestInformation | null> = yield call([signalR, signalR.getRtmpIngestInformation]);
        yield put(setLastServerAction({ method: 'getRtmpIngestInformation', result: rtmpIngestInformation }));
        yield put(rtmpIngestInformationUpdated(rtmpIngestInformation.data || null));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'getRtmpIngestInformation',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleRefreshSrtIngestInformation() {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const srtIngestInformation: ApiResponse<IngestInformation | null> = yield call([signalR, signalR.getSrtIngestInformation]);
        yield put(setLastServerAction({ method: 'getSrtIngestInformation', result: srtIngestInformation }));
        yield put(srtIngestInformationUpdated(srtIngestInformation.data || null));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'getSrtIngestInformation',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleStartPoll(action: PayloadAction<string>) {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.startPoll(action.payload);
        yield put(setLastServerAction({ method: 'startPoll', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'startPoll',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleClosePoll() {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.closePoll();
        yield put(setLastServerAction({ method: 'closePoll', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'closePoll',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleHidePoll() {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.presenter) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.hidePoll();
        yield put(setLastServerAction({ method: 'hidePoll', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'hidePoll',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleVoteOnPoll(action: PayloadAction<{ pollId: string; optionId: string }>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.voteOnPoll(action.payload.pollId, action.payload.optionId);
        yield put(setLastServerAction({ method: 'voteOnPoll', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'voteOnPoll',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleMarkChatChannelAsRead(action: PayloadAction<string>) {
    try {
        if (!action.payload || ChatChannelIds.isSystemChannel(action.payload)) {
            return;
        }

        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (!me || (me.clientType !== ClientType.moderator && me.clientType !== ClientType.presenter)) {
            return;
        }

        if (ChatChannelIds.isSystemChannel(action.payload)) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.markChatChannelAsRead(action.payload);
        yield put(setLastServerAction({ method: 'markChatChannelAsRead', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'markChatChannelAsRead',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleMarkChatChannelAsUnread(action: PayloadAction<string>) {
    try {
        if (!action.payload || ChatChannelIds.isSystemChannel(action.payload)) {
            return;
        }

        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (!me || (me.clientType !== ClientType.moderator && me.clientType !== ClientType.presenter)) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.markChatChannelAsUnread(action.payload);
        yield put(setLastServerAction({ method: 'markChatChannelAsUnread', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'markChatChannelAsUnread',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleUpdateNickname(action: PayloadAction<string>) {
    try {
        if (!action.payload) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.updateNickname(action.payload);
        yield put(setLastServerAction({ method: 'updateNickname', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'updateNickname',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleSetVideoLayout(action: PayloadAction<VideoLayout>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.setVideoLayout(action.payload);
        yield put(setLastServerAction({ method: 'setVideoLayout', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'setVideoLayout',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleClaimActivePresenter(action: PayloadAction) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.claimActivePresenter();
        yield put(setLastServerAction({ method: 'claimActivePresenter', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'claimActivePresenter',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleSetSpeakerStatus(action: PayloadAction<SpeakerStatus>) {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (me?.clientType !== ClientType.speaker) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.setStatus], action.payload);
        if (result.success) {
            yield put(mySpeakerStatusUpdated(action.payload));
        }
        yield put(setLastServerAction({ method: 'setSpeakerStatus', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'setSpeakerStatus',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleKickSpeaker(action: PayloadAction<string>) {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (!me || (me.clientType !== ClientType.moderator && me.clientType !== ClientType.presenter)) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.kickSpeaker], action.payload);
        yield put(setLastServerAction({ method: 'kickSpeaker', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'kickSpeaker',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleNotifySpeaker(action: PayloadAction<string>) {
    try {
        const me: ParticipantSession | null = yield select((state: RootState) => state.liveWebinar.me);
        if (!me || (me.clientType !== ClientType.moderator && me.clientType !== ClientType.presenter)) {
            return;
        }

        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.notifySpeaker], action.payload);
        yield put(setLastServerAction({ method: 'notifySpeaker', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'notifySpeaker',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleSendWebrtcIngestStats(action: PayloadAction<IWebrtcIngestStats>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.sendWebrtcIngestStats(action.payload);
        yield put(setLastServerAction({ method: 'sendWebrtcIngestStats', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'sendWebrtcIngestStats',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

// WebRTC mounts the moment moqFallbackTriggered flips moqPublisher, with whatever token is in the
// store. After an auth rejection that token is the one the server just refused, so fetch a fresh one
// first instead of racing the refresh. handleRefreshVindralChannel swallows its own errors, so the
// fallback always happens.
export function* handleMoqFallbackRequested(action: PayloadAction<{ reason: MoqFallbackReason; code: string | null }>) {
    if (isAuthRejection(action.payload.code)) {
        yield call(handleRefreshVindralChannel);
    }
    yield put(moqFallbackTriggered(action.payload));
}

// Best effort and deliberately silent: no setLastServerAction, so a missed sample never re-renders
// the page every second or shows up as a failed server action in the presenter's status panel.
function* handleSendMoqPublisherStats(action: PayloadAction<MoqPublisherStatsSample>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        yield call([signalR, signalR.sendMoqPublisherStats], action.payload);
    } catch {
        // Telemetry only.
    }
}

// Deliberately no setLastServerAction: that would re-render on every sample, and failures are
// retried by the queue rather than reported.
function* handlePlayerStatsSampled(action: PayloadAction<PlayerStatsSample>) {
    const signalR: LiveWebinarConnection = yield getContext('liveConnection');
    signalR.queuePlayerStats(action.payload);
}

function* handleSetIngestType(action: PayloadAction<IngestType>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: BaseApiResponse = yield signalR.setIngestType(action.payload);
        yield put(setLastServerAction({ method: 'setIngestType', result }));
    } catch (e) {
        yield put(
            setLastServerAction({
                method: 'setIngestType',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleExtendWebinarDuration(action: PayloadAction<Duration>) {
    try {
        const signalR: LiveWebinarConnection = yield getContext('liveConnection');
        const result: ApiResponse<boolean> = yield signalR.extendWebinarDuration(action.payload);
        if (result.success && result.data === true) {
            yield put(setWebinarExtendedResult(WebinarExtendedResult.Success));
        } else {
            yield put(setWebinarExtendedResult(WebinarExtendedResult.Failed));
        }
        yield put(setLastServerAction({ method: 'extendWebinarDuration', result }));
    } catch (e) {
        yield put(setWebinarExtendedResult(WebinarExtendedResult.Failed));
        yield put(
            setLastServerAction({
                method: 'extendWebinarDuration',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

export function* watchLiveWebinar() {
    yield takeEvery(joinWebinar, handleJoinWebinar);
    yield takeEvery(leaveWebinar, handleLeaveWebinar);
    yield takeEvery(setLiveStatus, handleSetLiveStatus);
    yield takeEvery(startWebinar, handleStartWebinar);
    yield takeEvery(stopWebinar, handleStopWebinar);

    yield takeEvery(serverTimeUpdated, handleServerTimeUpdated);
    yield takeEvery(setBroadcastState, handleSetBroadcastState);
    yield takeEvery(dataConnectionStatusUpdated, handleDataConnectionStatusUpdated);

    yield takeEvery(joinChatChannel, handleJoinChatChannel);
    yield takeEvery(leaveChatChannel, handleLeaveChatChannel);
    yield takeEvery(sendChatMessage, handleSendChatMessage);
    yield takeEvery(replyChatMessage, handleReplyChatMessage);
    yield takeEvery(forwardChatMessage, handleForwardChatMessage);
    yield takeEvery(deleteChatMessage, handleDeleteChatMessage);

    yield takeEvery(setActiveSlide, handleSetActiveSlide);
    yield takeEvery(setActiveChapter, handleSetActiveChapter);

    yield takeEvery(refreshVindralChannel, handleRefreshVindralChannel);
    yield takeEvery(refreshRtmpIngestInformation, handleRefreshRtmpIngestInformation);
    yield takeEvery(refreshSrtIngestInformation, handleRefreshSrtIngestInformation);

    yield takeEvery(startPoll, handleStartPoll);
    yield takeEvery(closePoll, handleClosePoll);
    yield takeEvery(hidePoll, handleHidePoll);
    yield takeEvery(voteOnPoll, handleVoteOnPoll);
    yield takeEvery(markChatChannelAsRead, handleMarkChatChannelAsRead);
    yield takeEvery(markChatChannelAsUnread, handleMarkChatChannelAsUnread);

    yield takeEvery(updateNickname, handleUpdateNickname);

    yield takeEvery(setVideoLayout, handleSetVideoLayout);

    yield takeEvery(claimActivePresenter, handleClaimActivePresenter);

    yield takeEvery(setSpeakerStatus, handleSetSpeakerStatus);
    yield takeEvery(kickSpeaker, handleKickSpeaker);
    yield takeEvery(notifySpeaker, handleNotifySpeaker);

    yield takeEvery(sendWebrtcIngestStats, handleSendWebrtcIngestStats);
    yield takeEvery(moqFallbackRequested, handleMoqFallbackRequested);
    yield takeEvery(sendMoqPublisherStats, handleSendMoqPublisherStats);
    yield takeEvery(playerStatsSampled, handlePlayerStatsSampled);
    yield takeEvery(setIngestType, handleSetIngestType);

    yield takeEvery(extendWebinarDuration, handleExtendWebinarDuration);
}
